import { useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  CircleDashed,
  ExternalLink,
  RefreshCw,
  Plug,
} from 'lucide-react';
import { useOnboardingIntegrations } from '../../hooks/useOnboarding';
import type { IntegrationStatus, IntegrationState } from '../../lib/onboardingApi';

// Step 0 — Integrations & Dependencies (CLE-130). Surfaces EVERY external
// integration the platform depends on (SmartBill, Google
// Calendar/Drive, Anthropic, OpenAI, SMTP), which env vars back it and which
// are still missing, plus a direct link to obtain the credentials. This ties
// "absolutely all dependencies — env-file and feature dependencies" into the
// onboarding flow, per the board's request. Credentials live server-side; this
// view only reports whether each env var is set, never its value.

const STATE_STYLES: Record<
  IntegrationState,
  { badge: string; label: string; icon: typeof CheckCircle2; iconClass: string }
> = {
  connected: {
    badge: 'border-green-200 bg-green-50 text-green-800',
    label: 'Conectat',
    icon: CheckCircle2,
    iconClass: 'text-green-600',
  },
  configured: {
    badge: 'border-green-200 bg-green-50 text-green-800',
    label: 'Configurat',
    icon: CheckCircle2,
    iconClass: 'text-green-600',
  },
  missing: {
    badge: 'border-amber-200 bg-amber-50 text-amber-800',
    label: 'Configurare lipsă',
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
  },
  optional: {
    badge: 'border-gray-200 bg-gray-50 text-gray-600',
    label: 'Opțional — neconfigurat',
    icon: CircleDashed,
    iconClass: 'text-gray-400',
  },
};

function IntegrationCard({ item }: { item: IntegrationStatus }) {
  const style = STATE_STYLES[item.state];
  const StateIcon = style.icon;
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-brand-50 p-2 text-brand-600">
            <StateIcon className={`h-5 w-5 ${style.iconClass}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {item.label}
              {item.required && <span className="ml-1 text-red-600">*</span>}
            </h3>
            <p className="mt-1 text-sm text-gray-500">{item.description}</p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${style.badge}`}
        >
          {style.label}
        </span>
      </div>

      {/* Env vars */}
      <div className="mt-4 space-y-1.5">
        {item.envVars.map((v) => (
          <div key={v.name} className="flex items-start gap-2 text-xs">
            {v.set ? (
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
            ) : v.required ? (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            ) : (
              <CircleDashed className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-300" />
            )}
            <div className="min-w-0">
              <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-700">
                {v.name}
              </code>
              {v.required && <span className="ml-1 text-[10px] text-red-500">(obligatoriu)</span>}
              {v.secret && <span className="ml-1 text-[10px] text-gray-400">(secret)</span>}
              {v.hint && <span className="ml-1 text-gray-500">— {v.hint}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Features unlocked */}
      {item.features.length > 0 && (
        <p className="mt-3 text-xs text-gray-500">
          <span className="font-medium text-gray-600">Activează:</span> {item.features.join(' · ')}
        </p>
      )}

      {item.notes && <p className="mt-2 text-xs text-gray-500">{item.notes}</p>}

      {item.setupUrl && (
        <a
          href={item.setupUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          Deschide pagina de configurare
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </section>
  );
}

export function StepDependencies() {
  const [probe, setProbe] = useState(false);
  const query = useOnboardingIntegrations(probe);
  const report = query.data;

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-brand-50 p-2 text-brand-600">
            <Plug className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">Integrări și dependențe</h3>
            <p className="mt-1 text-sm text-gray-500">
              Aici puteți vedea toate serviciile externe de care depinde platforma și ce trebuie
              configurat (chei API, variabile de mediu) pentru fiecare. Credențialele sunt setate pe
              server, în fișierul{' '}
              <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">.env</code>;
              ecranul arată doar dacă fiecare variabilă este setată, nu și valoarea ei.
            </p>
            {report && (
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  {report.summary.configured}/{report.summary.total} configurate
                </span>
                {report.summary.blocking > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    {report.summary.blocking} obligatorii lipsă
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setProbe(true)}
                  disabled={query.isFetching}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${query.isFetching ? 'animate-spin' : ''}`} />
                  Testează conexiunile
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {query.isLoading && <p className="text-sm text-gray-500">Se încarcă dependențele...</p>}
      {query.isError && (
        <p className="text-sm text-red-600">
          Lista de dependențe nu a putut fi încărcată
          {query.error instanceof Error ? `: ${query.error.message}` : ''}.
        </p>
      )}

      {report && (
        <div className="grid gap-4 md:grid-cols-2">
          {report.integrations.map((item) => (
            <IntegrationCard key={item.key} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
